import {
  CREDIT_PRICE,
  CreatePaymentRequestResp,
  CreditTxTypeEnum,
  CreditTxWithMeta,
  MAX_CREDIT_PER_TX,
  MIN_CREDIT_PER_TX,
  PurchaseMethodEnum,
  PurchaseStatusEnum
} from "@garther/interfaces";
import { Input, Popup, Separator } from "@garther/ui/components";
import { Button } from "@garther/ui/components";
import { useI18n } from "@garther/ui/libs";
import { useForm } from "@tanstack/react-form";
import { Building2, Code, Minus, Plus } from "lucide-react";
import { useEffect, useState } from "react";

import { creditApiClient } from "@/libs/api";

import { PaymentQRCodeDisplay } from "./qr-code-display";

interface PaymentPopupProps {
  isOpen: boolean;
  onClose: () => void;
  initialCreditAmount?: number;
  isTransactionPayment?: boolean;
  transaction?: CreditTxWithMeta;
}

// Fake data for development/testing
const FAKE_PAYMENT_RESPONSE: CreatePaymentRequestResp = {
  checkoutUrl: "https://pay.payos.vn/web/ab0fe73b7c5448dbb363f7ade1326eef",
  qrCode:
    "00020101021238610010A000000727013100069704520117101425090255237310208QRIBFTTA530370454061000005802VN62270823GARTHERCR8NKh0YvQdXYgHC63042B81",
  creditTx: {
    id: "t4Fu0OKbfHCHqyclb7yb",
    creditId: "8NKh0YvQdXYgHCXqV4fW6CwWMM23",
    amount: 2,
    createdAt: new Date("2025-09-02T02:35:58.051Z"),
    updatedAt: new Date("2025-09-02T02:35:58.051Z"),
    type: CreditTxTypeEnum.PURCHASE,
    desc: "Payment for 2 credits via bank_transfer",
    purchaseMeta: {
      creditTxId: "t4Fu0OKbfHCHqyclb7yb",
      method: PurchaseMethodEnum.BANK_TRANSFER,
      status: PurchaseStatusEnum.PENDING,
      currency: "VND",
      txId: "1756780556726694",
      qrCode:
        "00020101021238610010A000000727013100069704520117101425090255237310208QRIBFTTA530370454061000005802VN62270823GARTHERCR8NKh0YvQdXYgHC63042B81",
      checkoutUrl: "https://pay.payos.vn/web/ab0fe73b7c5448dbb363f7ade1326eef",
      expiresAt: new Date("2025-09-02T02:35:58.051Z"),
      createdAt: new Date("2025-09-02T02:35:58.051Z"),
      updatedAt: new Date("2025-09-02T02:35:58.051Z")
    },
    usageMeta: null,
    referralMeta: null
  }
};

export const PaymentPopup: React.FC<PaymentPopupProps> = ({
  isOpen,
  onClose,
  initialCreditAmount = MIN_CREDIT_PER_TX,
  isTransactionPayment = false,
  transaction
}) => {
  const { t, formatCurrency } = useI18n();
  const [paymentResponse, setPaymentResponse] = useState<CreatePaymentRequestResp | null>(null);
  const [isCreatingPayment, setIsCreatingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [isDevMode, setIsDevMode] = useState(false);

  const form = useForm<any>({
    defaultValues: {
      amount: initialCreditAmount,
      method: "bank-transfer"
    },
    onSubmit: async ({ value }: any) => {
      setIsCreatingPayment(true);
      setPaymentError(null);
      try {
        const paymentMethod =
          value.method === "stripe" ? PurchaseMethodEnum.CREDIT_CARD : PurchaseMethodEnum.BANK_TRANSFER;
        const paymentData = {
          amount: value.amount,
          method: paymentMethod
        };
        const response = await creditApiClient.createPaymentRequest(paymentData);
        setPaymentResponse(response);
      } catch (error) {
        console.error("Payment creation error:", error);
        setPaymentError(error instanceof Error ? error.message : "Failed to create payment request");
      } finally {
        setIsCreatingPayment(false);
      }
    }
  });

  // Sync initial amount when prop changes
  useEffect(() => {
    form.setFieldValue("amount", initialCreditAmount);
  }, [initialCreditAmount]);

  // If this is a transaction payment and we have transaction data, show QR code directly
  useEffect(() => {
    if (
      isTransactionPayment &&
      transaction &&
      (transaction.type === CreditTxTypeEnum.PURCHASE || transaction.type === CreditTxTypeEnum.PAYMENT) &&
      transaction.purchaseMeta
    ) {
      // Create a mock payment response from the existing transaction
      const mockResponse: CreatePaymentRequestResp = {
        checkoutUrl: transaction.purchaseMeta.checkoutUrl,
        qrCode: transaction.purchaseMeta.qrCode,
        creditTx: transaction
      };
      setPaymentResponse(mockResponse);
    }
  }, [isTransactionPayment, transaction]);

  const handlePaymentSelect = (method: "stripe" | "bank-transfer") => {
    form.setFieldValue("method", method);
    setPaymentError(null);
  };

  const handleDevModeToggle = () => {
    setIsDevMode(!isDevMode);
    if (!isDevMode) {
      // Set fake data when entering dev mode
      setPaymentResponse(FAKE_PAYMENT_RESPONSE);
    } else {
      // Clear fake data when exiting dev mode
      setPaymentResponse(null);
    }
  };

  const handleClose = () => {
    form.reset();
    setPaymentResponse(null);
    setPaymentError(null);
    setIsDevMode(false);
    onClose();
  };

  const amount = form.useStore((s) => s.values.amount);
  const selectedMethod = form.useStore((s) => s.values.method);
  const basePrice = amount * CREDIT_PRICE;
  const fee = selectedMethod === "stripe" ? 0.05 * basePrice : 0;
  const totalPrice = basePrice + fee;

  // Show QR code if payment was created successfully
  if (paymentResponse) {
    return (
      <Popup isOpen={isOpen} onClose={handleClose}>
        <PaymentQRCodeDisplay
          qrCodeData={paymentResponse.qrCode}
          checkoutUrl={paymentResponse.checkoutUrl}
          onClose={handleClose}
          creditTx={paymentResponse.creditTx}
        />
      </Popup>
    );
  }

  return (
    <Popup isOpen={isOpen} onClose={handleClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
        className="flex flex-col gap-4 max-w-md"
      >
        {/* Header */}
        <div>
          <div className="flex items-center justify-between">
            <h2 className="text-responsive-lg font-normal text-text-primary">{t("credits.payment.title")}</h2>
          </div>
          <p className="text-responsive-base text-text-secondary">{t("credits.payment.description")}</p>
        </div>

        {/* Error Message */}
        {paymentError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-responsive-sm">
            {paymentError}
          </div>
        )}

        {/* Credit Amount Selection */}
        <form.Field
          name="amount"
          validators={{
            onChange: ({ value }) =>
              Number.isFinite(value)
                ? Math.max(MIN_CREDIT_PER_TX, Math.min(MAX_CREDIT_PER_TX, value)) !== value
                  ? t("credits.payment.errors.amountBounded", {
                      min: MIN_CREDIT_PER_TX,
                      max: MAX_CREDIT_PER_TX
                    })
                  : undefined
                : t("profile.creation.errors.nameRequired")
          }}
        >
          {(field) => (
            <div className="flex flex-col space-y-1">
              <label className="block text-responsive-base font-normal text-text-secondary mb-2">
                {t("credits.payment.amountLabel")}
              </label>
              <div className="flex items-center space-x-3 w-full justify-between">
                <Button
                  variant="outline"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    field.handleChange(field.state.value - 10);
                  }}
                  isDisabled={field.state.value <= MIN_CREDIT_PER_TX}
                  className="w-8 h-8 !p-0"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <div className="flex items-center justify-center w-[60%] gap-1">
                  <Input
                    containerClassName="flex-1"
                    value={field.state.value}
                    className="text-responsive-md h-8 border-none font-normal p-0 text-right mr-1 w-full"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const next = Number(e.target.value);
                      field.handleChange(Number.isFinite(next) ? next : 20);
                    }}
                  />
                  <p className="flex-1">{t("common.unit")}</p>
                </div>
                <Button
                  variant="outline"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    field.handleChange(field.state.value + 10);
                  }}
                  isDisabled={field.state.value >= MAX_CREDIT_PER_TX}
                  className="w-8 h-8 !p-0"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {field.state?.meta?.errors?.[0] && (
                <p className="text-responsive-sm text-red-400">{field.state?.meta?.errors?.[0]}</p>
              )}
            </div>
          )}
        </form.Field>

        <Separator />

        {/* Payment Options */}
        <div className="space-y-3">
          {/* Stripe Option */}
          {/* <div
						className={`p-4 border rounded-sm cursor-pointer transition-all ${selectedMethod === "stripe"
							? "border-border-dark bg-background-highlight"
							: "border-border-light hover:border-border-dark"
							}`}
						onClick={() => handlePaymentSelect("stripe")}
					>
						<div className="flex items-center space-x-3">
							<div className="p-2 rounded-sm bg-background-dark">
								<CreditCard className="w-5 h-5 text-text-inverse" />
							</div>
							<div className="flex-1">
								<h3 className="text-responsive-lg font-normal text-text-primary">
									{t("credits.payment.stripe.title")}
								</h3>
								<p className="text-responsive-sm font-normal text-text-secondary">
									{t("credits.payment.stripe.description")}
								</p>
							</div>
						</div>
					</div> */}

          {/* Bank Transfer Option */}
          <div
            className={`p-4 border rounded-sm cursor-pointer transition-all ${
              selectedMethod === "bank-transfer"
                ? "border-border-dark bg-background-highlight"
                : "border-border-light hover:border-border-dark"
            }`}
            onClick={() => handlePaymentSelect("bank-transfer")}
          >
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-sm bg-background-dark-secondary">
                <Building2 className="w-5 h-5 text-text-inverse" />
              </div>
              <div className="flex-1">
                <h3 className="text-responsive-base font-normal text-text-primary">
                  {t("credits.payment.bankTransfer.title")}
                </h3>
                <p className="text-responsive-sm font-medium text-text-secondary">
                  {t("credits.payment.bankTransfer.description")}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-b border-border-light"></div>

        {/* Total Price Display */}
        <div>
          <div className="flex items-center justify-between">
            <p className="text-responsive-base font-normal text-text-secondary">
              {t("credits.payment.basePrice")}
            </p>

            <p className="text-responsive-base font-normal text-text-primary">
              {formatCurrency(basePrice).toLocaleString()}
            </p>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-responsive-base font-normal text-text-secondary">
              {t("credits.payment.stripeFee")}
            </p>

            <p className="text-responsive-base font-normal text-text-primary">
              {formatCurrency(fee).toLocaleString()}
            </p>
          </div>
        </div>
        <div className="border-b border-border-light"></div>
        <div>
          <div className="flex items-center justify-between">
            <p className="text-responsive-base font-normal text-text-secondary">
              {t("credits.payment.finalPrice")}
            </p>

            <p className="text-responsive-base font-normal text-text-primary">
              {formatCurrency(totalPrice).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Dev Mode Button */}
        <div className="flex justify-center hidden">
          <Button
            variant={isDevMode ? "default" : "ghost"}
            onClick={handleDevModeToggle}
            className={`${isDevMode ? "bg-orange-500 hover:bg-orange-600 text-white" : "text-text-secondary hover:text-text-primary"}`}
          >
            <Code className="w-4 h-4 mr-2" />
            {isDevMode ? "Exit Dev Mode" : "Dev Mode"}
          </Button>
        </div>
        {isDevMode && (
          <div className="text-center">
            <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
              <Code className="w-3 h-3 mr-1" />
              Development Mode Active
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col-reverse sm:flex-row gap-3">
          <Button variant="outline" onClick={handleClose} isDisabled={isCreatingPayment} className="flex-1">
            {t("common.cancel")}
          </Button>
          <Button
            variant="default"
            type="submit"
            isDisabled={!selectedMethod || amount <= 0 || isCreatingPayment}
            className="flex-1"
          >
            {isCreatingPayment ? (
              <div className="e-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                <span>{t("common.loading")}</span>
              </div>
            ) : (
              t("credits.payment.proceed")
            )}
          </Button>
        </div>
      </form>
    </Popup>
  );
};
